import { type FunctionComponent, type HTMLAttributes } from 'react';
import { type ProductVariantFieldsFragment, type ProjectProductFinishFieldsFragment } from '../../../__generated__/graphql-client-types';
import { handleKeys } from '../../../helpers/keyboard/keyboard.helper';
import { getSwatchStyle } from '../../../helpers/product-helper/product.helper';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import WarningIcon from '../../svgs/warning.svg';

const DEFAULT_CLASSES = `flex flex-column relative ba br2 bw1 hover-b--theme-primary pointer outline-0`;

type VariantSwatchProps = {
	warning?: boolean;
	size?: number;
	color?: string;
	swatchHeight?: number;
} & (ProductVariantFieldsFragment | ProjectProductFinishFieldsFragment);

export type VariantProps = {
	onSelectVariant: Function;
	onHover?: Function;
	variant: VariantSwatchProps;
	currentVariant: ProductVariantFieldsFragment | ProjectProductFinishFieldsFragment | undefined;
	showRestrictionWarning?: boolean;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

export const Variant: FunctionComponent<VariantProps> = ({
	variant,
	currentVariant,
	onSelectVariant,
	onHover,
	showRestrictionWarning = false,
	className = ''
}) => {
	const isActive = currentVariant?.id === variant.id;

	const handleSelect = () => {
		onSelectVariant(variant);
	};

	const handleMouseEnter = () => {
		if (onHover) {
			onHover(variant);
		}
	};

	const handleMouseLeave = () => {
		if (onHover) {
			onHover(null);
		}
	};

	return (
		<div
			key={variant.id}
			data-testid={variant.id}
			data-automation="finish-swatch"
			role="checkbox"
			aria-checked={isActive}
			tabIndex={0}
			className={`dib outline-0 ${className}`}
			onClick={handleSelect}
			onKeyDown={handleKeys(['Enter', ' '], handleSelect)}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}>
			<div className={`${DEFAULT_CLASSES} ${isActive ? 'b--theme-primary' : 'b--theme-grey-lighter'}`} data-testid="variantContent">
				<VariantSwatch {...variant} warning={showRestrictionWarning} />
			</div>
		</div>
	);
};

const VariantSwatch: FunctionComponent<VariantSwatchProps> = ({ image, color, swatchGradient, swatchHeight, size = 90, warning }) => (
	<div className="relative">
		{warning && <WarningIcon className="absolute z-1 f5 f4-ns ma1 theme-warning" data-testid="WarningIcon" />}
		<CloudinaryImage
			publicID={image.id}
			description={image.description}
			options={{ type: image.imageType, height: size, width: size }}
			bypassLazyLoad={false}
		/>
		<div className="br1 br-bottom absolute bottom-0 z-0 w-100" style={getSwatchStyle(swatchGradient, color, swatchHeight)} />
	</div>
);
